/**
 * Public server functions for CMS-backed pages (blog, docs, legal).
 * Read-only and published-content-only; the admin CRUD lives in
 * lib/cms/admin/server.ts, and the dashboard announcement banner reads the
 * JSON API route (one read path, one cache policy). Not marked `server-only`:
 * the client imports these as RPC stubs and the compiler strips the handler
 * bodies (and the server-only query module) from the client bundle.
 *
 * Slug inputs are checked with isValidSlug and treated as NOT FOUND when
 * malformed (crawlers produce uppercase/dotted URLs; those must 404, not hit
 * the error boundary), so the validators here only bound the string.
 */
import { createServerFn } from '@tanstack/react-start'
import { z } from 'zod'

import {
  type DocsNav,
  type GuideDetail,
  type LegalPageContent,
  type PostDetail,
  type PostListItem,
  queryDocsNav,
  queryGuide,
  queryLegalPage,
  queryPublishedPost,
  queryPublishedPosts,
} from '@/lib/cms/queries.server'
import { parseInput } from '@/lib/validation'
import { type DocsNavGroup, isValidSlug } from './cms'

const slugInput = (d: { slug: string }) =>
  parseInput(z.object({ slug: z.string().max(200) }), d)

/** Published posts, newest first, for the blog index. */
export const listPublishedPosts = createServerFn({ method: 'GET' }).handler(
  async (): Promise<PostListItem[]> => queryPublishedPosts(),
)

/** One published post with its markdown body, or null (route 404s). */
export const getPublishedPost = createServerFn({ method: 'GET' })
  .validator(slugInput)
  .handler(
    async ({ data }): Promise<PostDetail | null> =>
      isValidSlug(data.slug) ? queryPublishedPost(data.slug) : null,
  )

/** Published guides grouped for the docs sidebar, plus the flat reading
 *  order the guide pages use for prev/next. */
export const getDocsNav = createServerFn({ method: 'GET' }).handler(
  async (): Promise<DocsNav> => queryDocsNav(),
)

/** One published guide with its body, or null (route 404s). Prev/next come
 *  from the /docs layout's nav data, not from here. */
export const getPublishedGuide = createServerFn({ method: 'GET' })
  .validator(slugInput)
  .handler(
    async ({ data }): Promise<Omit<GuideDetail, 'prev' | 'next'> | null> =>
      isValidSlug(data.slug) ? queryGuide(data.slug) : null,
  )

/** One published legal page (terms, privacy, …), or null (route 404s). */
export const getLegalPage = createServerFn({ method: 'GET' })
  .validator(slugInput)
  .handler(
    async ({ data }): Promise<LegalPageContent | null> =>
      isValidSlug(data.slug) ? queryLegalPage(data.slug) : null,
  )

export type { DocsNav, DocsNavGroup }
